import { useEffect, useState } from "react"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { fetchMessageTemplates, updateMessageTemplate } from "~/apis/messageTemplates"
import { PageSpinner } from "~/components/Spinner"

const KIND_LABELS = {
  welcome: "Welcome (guest, sent on arrival day)",
  checkout: "Check-out note (guest, sent on check-out morning)",
  clean_request_back_to_back: "Clean request — back-to-back, 11am–3pm (cleaner)",
  clean_request_scheduled: "Clean request — set date and time (cleaner)",
  clean_request_changeover: "Clean request — either of two days (cleaner)",
  clean_request_cleaner_chooses: "Clean request — cleaner picks the day (cleaner)",
}

function TemplateEditor({ template }) {
  const [ body, setBody ] = useState(template.body)
  const [ saved, setSaved ] = useState(false)
  const [ error, setError ] = useState(null)

  async function save() {
    setError(null)
    try {
      await updateMessageTemplate(template.id, body)
      setSaved(true)
      setTimeout(() => setSaved(false), 1500)
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="mb-4">
      <p className="mb-1 text-sm font-medium text-foreground">{KIND_LABELS[template.kind] || template.kind}</p>
      {error && <p className="mb-1 text-sm text-destructive">{error}</p>}
      <Textarea value={body} onChange={(event) => setBody(event.target.value)} rows={4} />
      <Button
        size="sm"
        className="mt-1"
        onClick={save}
        disabled={body === template.body && !saved}
      >
        {saved ? "Saved ✓" : "Save"}
      </Button>
    </div>
  )
}

function PlaceholderLegend({ placeholders }) {
  return (
    <p className="mb-3 text-xs text-muted-foreground">
      Placeholders:{" "}
      {placeholders.map((placeholder) => (
        <code key={placeholder} className="mr-1 mt-1 inline-block rounded bg-muted px-1.5 py-0.5">{`{{${placeholder}}}`}</code>
      ))}
    </p>
  )
}

// Every editable message the app sends — guest messages and the cleaner
// ask-messages — grouped by property.
export default function TemplateEditors() {
  const [ data, setData ] = useState(null)
  const [ loadError, setLoadError ] = useState(null)

  useEffect(() => {
    fetchMessageTemplates().then(setData).catch((err) => setLoadError(err.message))
  }, [])

  if (loadError) return <p className="text-sm text-destructive">Couldn't load: {loadError}</p>
  if (!data) return <PageSpinner />

  const byProperty = data.templates.reduce((acc, template) => {
    (acc[template.property_name] = acc[template.property_name] || []).push(template)
    return acc
  }, {})

  return (
    <div>
      {Object.entries(byProperty).map(([ propertyName, templates ]) => (
        <section key={propertyName} className="mb-4 rounded-xl border border-border p-4">
          <h3 className="mb-3 font-semibold text-foreground">{propertyName}</h3>

          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Guest messages (texted to Riparide and direct guests)</h4>
          <PlaceholderLegend placeholders={data.guest_placeholders} />
          {templates.filter((template) => template.audience === "guest").map((template) => (
            <TemplateEditor key={template.id} template={template} />
          ))}

          <h4 className="mb-1 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cleaner messages</h4>
          <PlaceholderLegend placeholders={data.cleaner_placeholders} />
          {templates.filter((template) => template.audience === "cleaner").map((template) => (
            <TemplateEditor key={template.id} template={template} />
          ))}
        </section>
      ))}
    </div>
  )
}
